import { defaultPlaceholderFieldName, defaultPlaceholderOperatorName } from '../../defaults';
import { uniqByName } from '../../internal/uniq';
import type {
  ExportFormat,
  FormatQueryOptions,
  QueryValidator,
  RuleGroupType,
  RuleGroupTypeAny,
  RuleType,
  RuleValidator,
  ValidationMap,
  ValidationResult,
} from '../../types/index.noReact';
import { convertFromIC } from '../convertQuery';
import { isRuleOrGroupValid } from '../isRuleOrGroupValid';
import { defaultValueProcessorByRule } from './defaultValueProcessorByRule';
import { defaultValueProcessorMongoDBByRule } from './defaultValueProcessorMongoDBByRule';

/**
 * Formats a query in the requested output format.
 */
// function formatQuery(ruleGroup: RuleGroupTypeAny): string;
// function formatQuery(
//   ruleGroup: RuleGroupTypeAny,
//   options: Omit<FormatQueryOptions, 'format'>
// ): string;
function formatQuery(
  ruleGroup: RuleGroupTypeAny,
  options: FormatQueryOptions | ExportFormat = 'mongodb'
) {
  let format: ExportFormat = 'json';
  let valueProcessorInternal = defaultValueProcessorByRule;
  let validator: QueryValidator = () => true;
  let fields: Required<FormatQueryOptions>['fields'] = [];
  let validationMap: ValidationMap = {};
  let fallbackExpression = '';
  let parseNumbers = false;
  let placeholderFieldName = defaultPlaceholderFieldName;
  let placeholderOperatorName = defaultPlaceholderOperatorName;

  if (typeof options === 'string') {
    format = options.toLowerCase() as ExportFormat;
    valueProcessorInternal = defaultValueProcessorMongoDBByRule;
  } else {
    format = (options.format ?? 'json').toLowerCase() as ExportFormat;
    valueProcessorInternal = defaultValueProcessorMongoDBByRule;
    validator = options.validator ?? (() => true);
    fields = options.fields ?? [];
    fallbackExpression = options.fallbackExpression ?? '';
    parseNumbers = !!options.parseNumbers;
    placeholderFieldName = options.placeholderFieldName ?? defaultPlaceholderFieldName;
    placeholderOperatorName = options.placeholderOperatorName ?? defaultPlaceholderOperatorName;
  }
  if (!fallbackExpression) {
    fallbackExpression = '';
  }
  // istanbul ignore else
  if (typeof validator === 'function') {
    const validationResult = validator(ruleGroup);
    if (typeof validationResult === 'boolean') {
      if (validationResult === false) {
        return format === 'mongodb' ? `{${fallbackExpression}}` : fallbackExpression;
      }
    } else {
      validationMap = validationResult;
    }
  }

  const validatorMap: Record<string, RuleValidator> = {};
  const uniqueFields = uniqByName(fields);
  uniqueFields.forEach(f => {
    // istanbul ignore else
    if (typeof f.validator === 'function') {
      validatorMap[f.name] = f.validator;
    }
  });

  const validateRule = (rule: RuleType) => {
    let validationResult: boolean | ValidationResult | undefined = undefined;
    let fieldValidator: RuleValidator | undefined = undefined;
    if (rule.id) {
      validationResult = validationMap[rule.id];
    }
    if (fields.length) {
      const fieldArr = fields.filter(f => f.name === rule.field);
      if (fieldArr.length) {
        const field = fieldArr[0];
        // istanbul ignore else
        if (typeof field.validator === 'function') {
          fieldValidator = field.validator;
        }
      }
    }
    return [validationResult, fieldValidator] as const;
  };

  const processRuleGroup = ({
    rg,
    outermost = false,
    parentCombinator,
    parentSurrounded = false,
  }: {
    rg: RuleGroupType;
    outermost?: boolean;
    parentCombinator?: string;
    parentSurrounded?: boolean;
  }) => {
    if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */ ''])) {
      return outermost ? fallbackExpression : '';
    }

    const combinator = `"$${rg.combinator}"`;
    let sameFieldInMultipleRules = false;
    (rg.rules as Array<{ field: string }>).reduce(
      (accFields: string[], rule: { field: string }) => {
        if (rule.field) {
          if (accFields.indexOf(rule.field) > -1) {
            sameFieldInMultipleRules = true;
          }
        }
        accFields.push(rule.field);
        return accFields;
      },
      []
    );

    const expression: string = rg.disabled
      ? ''
      : rg.rules
          .map(rule => {
            if (rule.disabled) return '';
            if ('rules' in rule) {
              const processedRuleGroup = processRuleGroup({
                rg: rule,
                parentCombinator: rg.combinator,
                parentSurrounded: sameFieldInMultipleRules,
              });
              return processedRuleGroup ? `${processedRuleGroup}` : '';
            }
            const [validationResult, fieldValidator] = validateRule(rule);
            if (
              !isRuleOrGroupValid(rule, validationResult, fieldValidator) ||
              rule.field === placeholderFieldName ||
              rule.operator === placeholderOperatorName
            ) {
              return '';
            }
            const condition = valueProcessorInternal(rule, {
              parseNumbers,
              escapeQuotes: (rule.valueSource ?? 'value') === 'value',
            });
            if (!outermost || rg.combinator === 'or' || sameFieldInMultipleRules) {
              return `{${condition}}`;
            }
            return condition;
          })
          .filter(Boolean)
          .join(',');

    if (expression) {
      if (parentSurrounded || parentCombinator == 'or') {
        return `{${combinator}:[${expression}]}`;
      } else if (outermost && rg.combinator === 'and' && !sameFieldInMultipleRules) {
        return `${expression}`;
      } else {
        return `${combinator}:[${expression}]`;
      }
    } else {
      return fallbackExpression;
    }
  };

  const rgStandard = 'combinator' in ruleGroup ? ruleGroup : convertFromIC(ruleGroup);
  return `{${processRuleGroup({ rg: rgStandard, outermost: true })}}`;
}

export { formatQuery };
